//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import {
  PinterestGatewayBoard,
  PinterestGatewayUser,
} from '@bvengineering/pinterest-gateway-service-types';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import * as pinterestUserApi from '@apis/pinterest-user/pinterestUserApi';
import { getPinterestUserFlags } from '@pinterest/redux/selectors/getPinterestUser';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { RequestError } from '@utils/sessionFetch';

import {
  IPinterestUserState,
  MAX_BOARD_PAGES,
} from 'app/pinterest/redux/user/pinterestUserModel';

const initialState: IPinterestUserState = {
  boards: [],
  user: undefined,

  errors: {
    fetchUserBoardsError: undefined,
    fetchUserError: undefined,
  },
  flags: {
    hasFetchedUserBoards: false,
    isFetchingUserBoards: false,
    isFetchingUser: false,
  },
};

const { actions, reducer } = createSlice({
  name: SliceNames.PinterestUser,
  initialState,
  reducers: {
    requestBoards(state) {
      state.errors.fetchUserBoardsError = undefined;
      state.flags.isFetchingUserBoards = true;
    },
    receiveBoardsSucceeded(
      state,
      action: PayloadAction<PinterestGatewayBoard[]>,
    ) {
      state.flags.isFetchingUserBoards = false;
      state.boards = action.payload;
      state.flags.hasFetchedUserBoards = true;
    },
    receiveBoardsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingUserBoards = false;
      state.errors.fetchUserBoardsError = action.payload;
    },
    requestUser(state) {
      state.errors.fetchUserError = undefined;
      state.flags.isFetchingUser = true;
    },
    receiveUserSucceeded(
      state,
      action: PayloadAction<PinterestGatewayUser | undefined>,
    ) {
      state.flags.isFetchingUser = false;
      state.user = action.payload;
    },
    receiveUserFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingUser = false;
      state.errors.fetchUserError = action.payload;
    },
  },
});

const fetchAllBoards = async (
  bookmark?: string,
  page: number = 0,
): Promise<PinterestGatewayBoard[]> => {
  const { boards, bookmark: nextBookmark } =
    await pinterestUserApi.getBoardsPaged(bookmark);

  if (!!nextBookmark && boards.length > 0 && page < MAX_BOARD_PAGES) {
    const nextBoards = await fetchAllBoards(nextBookmark, page + 1);
    return [...boards, ...nextBoards];
  }
  return boards;
};

export const fetchBoards = createThunk(() => async (dispatch) => {
  dispatch(actions.requestBoards());

  try {
    const boards = await fetchAllBoards();
    dispatch(
      actions.receiveBoardsSucceeded(
        boards.sort((a, b) =>
          (a.name?.toLowerCase() ?? '') < (b.name?.toLowerCase() ?? '')
            ? -1
            : 1,
        ),
      ),
    );
  } catch (e) {
    dispatch(actions.receiveBoardsFailed((e as RequestError).message));
  }
});

export const fetchUser = createThunk(() => async (dispatch, getState) => {
  const { isFetchingUser } = getPinterestUserFlags(getState());
  // If we are currently already attempting to fetch the current user, do not try to fetch again
  if (isFetchingUser) {
    return;
  }
  dispatch(actions.requestUser());

  try {
    const user = await pinterestUserApi.getUser();
    dispatch(actions.receiveUserSucceeded(user));
  } catch (e) {
    dispatch(actions.receiveUserFailed((e as RequestError).message));
  }
});

export default reducer;
